<template>
  <v-container>
    <v-form
    ref="vatRecordForm"
    v-model="valid"
    lazy-validation>
        <v-row justify="start">
            <v-col cols="12" md="4">
                <span style="font-weight:bold" v-if="!draft">{{ $t('groupVatRecordDetails') }}</span>
                <span style="font-weight:bold" v-else>{{ $t('createGroupVatRecord') }}</span>
            </v-col>
            <v-col cols="12" md="4" align="right">
                <v-tooltip right color="#2b5d66">
                    <template v-slot:activator="{ on, attrs }">
                        <v-icon 
                        v-show="vatRecord.versions != null && vatRecord.versions.length > 1"
                        v-bind="attrs"
                        v-on="on" 
                        @click="showVersion = !showVersion"
                        >
                            history
                        </v-icon>
                    </template>
                    <span>{{ $t('vatRecordVersions') }}</span>
                </v-tooltip>
            </v-col>
        </v-row>
        <v-row justify="space-around">
            <v-col cols="4" md="4">
                <v-textarea
                auto-grow
                rows="1"
                :disabled="!draft"
                v-bind:label="$t('calculationNumber')"
                v-model.trim="vatRecord.calculationNumber"
                :rules="documentNumberRule()"
                @input="checkDocumentNumber()"
                >
                </v-textarea> 
                <br />
                <span style="font-weight:bold;">{{ $t('status') }}: {{ documentStatusText() }}</span><br /><br />   
            </v-col>
            <v-col cols="4" md="4">
                <v-select
                v-model="vatRecord.periodType.vatPeriodTypeId"
                :items="vatPeriodTypeItems"
                :item-text="getVatPeriodTypeText"
                item-value="vatPeriodTypeId"
                :rules="[v => !!v || $t('timeRangeRequired')]"
                :disabled="!(draft || correction)"
                v-bind:label="$t('timeRange')"
                v-bind:no-data-text="$t('noData')"
                @change="periodTypeChanged"
                required
                >
                </v-select>
                <v-select
                v-model="vatRecord.period"
                :items="vatPeriodItems"
                :item-text="getVatPeriodText"
                item-value="vatPeriodId" 
                :rules="[v => !!v || $t('periodRequired')]"
                :disabled="!(draft || correction)"
                v-bind:label="$t('period')"
                v-bind:no-data-text="$t('noData')"
                return-object
                required
                >
                </v-select>
            </v-col>
            <v-col cols="4" md="4">   
                <v-text-field
                v-model="vatRecord.year"
                v-bind:label="$t('year')"
                :disabled="!(draft || correction)"
                :rules="[v => !!v || $t('yearRequired')]"
                clearable>
                </v-text-field>    
                <v-menu
                v-model="menuTurnoverDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
                >
                <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                    disabled
                    v-model="vatRecord.turnoverDate"
                    v-bind:label="$t('turnoverDate')"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    clearable
                    @click:clear="clearTurnoverDate"
                    :rules="[v => !!v || $t('required')]"
                    v-bind="attrs"
                    v-on="on"
                    ></v-text-field>
                </template>
                <v-date-picker
                    v-model="vatRecord.turnoverDate"
                    no-title
                    scrollable
                    min="2017-01-01"
                    max="2050-01-01"
                    @input="menuTurnoverDate = false"
                >
                </v-date-picker>
                </v-menu>           
            </v-col>
        </v-row>
        <br /><br />
        <v-row justify="space-around">
            <!-- PAYMENT -->
            <v-col cols="12">
                <v-expansion-panels
                v-model="prepaymentPanel"
                >
                <v-expansion-panel>
                    <v-expansion-panel-header style="font-weight:bold">{{ $t('prepaymetTurnover')}}</v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">                                    
                                    {{ $t('prepaymentTurnover20')}}
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.prepaymentAmount20"
                                    v-bind:label="$t('prepaymentAmount')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    @input="calculateTotal()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    disabled
                                    v-model="vatRecord.prepaymentBaseAmount20"
                                    v-bind:label="$t('baseAmount')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    disabled
                                    v-model="vatRecord.prepaymentCalculatedVat20"
                                    v-bind:label="$t('vatAmount')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    {{ $t('prepaymentTurnove10')}}
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.prepaymentAmount10"
                                    v-bind:label="$t('prepaymentAmount')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    @input="calculateTotal()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    disabled
                                    v-model="vatRecord.prepaymentBaseAmount10"
                                    v-bind:label="$t('baseAmount')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    disabled
                                    v-model="vatRecord.prepaymentCalculatedVat10"
                                    v-bind:label="$t('baseAmount')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-content>
                </v-expansion-panel>
                </v-expansion-panels>
            </v-col>                
        </v-row>
        <v-row justify="space-around">
            <!-- TURNOVERWITHFEE -->
            <v-col cols="12">
                <v-expansion-panels
                v-model="turnoverWithFeePanel"
                >
                <v-expansion-panel>
                    <v-expansion-panel-header style="font-weight:bold">{{ $t('turnoverWithFee')}}</v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <v-row justify="space-around">
                                <v-col cols="3"  align-self="center">
                                    {{ $t('turnover20GroupVat')}}
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.baseAmount20"
                                    v-bind:label="$t('baseAmount')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.calculatedVat20"
                                    v-bind:label="$t('vatAmount')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    {{ $t('turnover10GroupVat')}}
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.baseAmount10"
                                    v-bind:label="$t('baseAmount')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.calculatedVat10"
                                    v-bind:label="$t('vatAmount')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-content>
                </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
            </v-row>
            <v-row justify="space-around">
            <!-- TURNOVERWITHOUTFEE -->
            <v-col cols="12">
                <v-expansion-panels
                v-model="turnoverWithoutFeePanel"
                >
                <v-expansion-panel>
                    <v-expansion-panel-header style="font-weight:bold">{{ $t('turnoverWitoutFee')}}</v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    {{ $t('turnover20GroupVat')}}
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.noChargeBaseAmount20"
                                    v-bind:label="$t('baseAmount')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.noChargeCalculatedVat20"
                                    v-bind:label="$t('vatAmount')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    {{ $t('turnover10GroupVat')}}
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.noChargeBaseAmount10"
                                    v-bind:label="$t('baseAmount')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.noChargeCalculatedVat10"
                                    v-bind:label="$t('vatAmount')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-content>
                </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
            </v-row>
            <v-row justify="space-around">
            <!-- SPECIALVAT -->
            <v-col cols="12">
                <v-expansion-panels
                v-model="specialVatPanel">
                <v-expansion-panel>
                    <v-expansion-panel-header style="font-weight:bold">{{ $t('specialVat')}}</v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <v-row justify="space-around">
                                <v-col cols="12" style="font-weight:bold">                                   
                                    {{ $t('touristServices')}}
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.touristServicesBaseAmount20"
                                    v-bind:label="$t('turnoverBaseAmoutn20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.touristServicesCalculatedVat20"
                                    v-bind:label="$t('calculatedVatAmount20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.prepaymentTouristServicesBaseAmount20"
                                    v-bind:label="$t('turnoverBaseAmountPrepayment20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.prepaymentTouristServicesCalculatedVat20"
                                    v-bind:label="$t('calculatedVatAmountPrepayment20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="12" style="font-weight:bold">                                   
                                    {{ $t('artAntiquesCollectibleGoods')}}
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.artAntiquesCollectibleGoodsBaseAmount20"
                                    v-bind:label="$t('turnoverBaseAmoutn20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.artAntiquesCollectibleGoodsCalculatedVat20"
                                    v-bind:label="$t('calculatedVatAmount20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.artAntiquesCollectibleGoodsBaseAmount10"
                                    v-bind:label="$t('turnoverBaseAmoutn10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.artAntiquesCollectibleGoodsCalculatedVat10"
                                    v-bind:label="$t('calculatedVatAmount10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.prepaymentArtAntiquesCollectibleGoodsBaseAmount20"
                                    v-bind:label="$t('turnoverBaseAmountPrepayment20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.prepaymentArtAntiquesCollectibleGoodsCalculatedVat20"
                                    v-bind:label="$t('calculatedVatAmountPrepayment20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.prepaymentArtAntiquesCollectibleGoodsBaseAmount10"
                                    v-bind:label="$t('turnoverBaseAmountPrepayment10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.prepaymentArtAntiquesCollectibleGoodsCalculatedVat10"
                                    v-bind:label="$t('calculatedVatAmountPrepayment10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format()"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-content>
                </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
            </v-row>
            <v-row justify="space-around">
            <!-- SupplierTaxDebtorTurnover -->
            <v-col cols="12">
                <v-expansion-panels
                v-model="supplierTaxDebtorTurnoverPanel">
                <v-expansion-panel>
                    <v-expansion-panel-header style="font-weight:bold">{{ $t('supplierTaxDebtorTurnover')}}</v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <v-row justify="space-around">
                                <v-col cols="12" style="font-weight:bold">                                   
                                    {{ $t('baseIncrease')}}
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.supplierTaxDebtorTurnover.increaseBaseAmount20"
                                    v-bind:label="$t('baseIncrease20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.supplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.supplierTaxDebtorTurnover.increaseVat20"
                                    v-bind:label="$t('vatIncrease20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.supplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.supplierTaxDebtorTurnover.increaseBaseAmount10"
                                    v-bind:label="$t('baseIncrease10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.supplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.supplierTaxDebtorTurnover.increaseVat10"
                                    v-bind:label="$t('vatIncrease10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.supplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="12" style="font-weight:bold">                                   
                                    {{ $t('baseReduction')}}
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.supplierTaxDebtorTurnover.reductionBaseAmount20"
                                    v-bind:label="$t('baseReduction20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.supplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.supplierTaxDebtorTurnover.reductionVat20"
                                    v-bind:label="$t('vatReduction20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.supplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.supplierTaxDebtorTurnover.reductionBaseAmount10"
                                    v-bind:label="$t('baseReduction10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.supplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.supplierTaxDebtorTurnover.reductionVat10"
                                    v-bind:label="$t('vatReduction10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.supplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.supplierTaxDebtorTurnover.prepaymentReductionBaseAmount20"
                                    v-bind:label="$t('baseReductionPrepayment20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.supplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.supplierTaxDebtorTurnover.prepaymentReductionVat20"
                                    v-bind:label="$t('vatReductionPrepayment20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.supplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.supplierTaxDebtorTurnover.prepaymentReductionBaseAmount10"
                                    v-bind:label="$t('baseReductionPrepayment10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.supplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.supplierTaxDebtorTurnover.prepaymentReductionVat10"
                                    v-bind:label="$t('vatReductionPrepayment10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.supplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-content>
                </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
            </v-row>
            <v-row justify="space-around">
            <!-- ReceiverTaxDebtorTurnover -->
            <v-col cols="12">
                <v-expansion-panels
                v-model="receiverTaxDebtorTurnoverPanel">
                <v-expansion-panel>
                    <v-expansion-panel-header style="font-weight:bold">{{ $t('receiverTaxDebtorTurnover')}}</v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <v-row justify="space-around">
                                <v-col cols="12" style="font-weight:bold">                                   
                                    {{ $t('baseIncrease')}}
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.receiverTaxDebtorTurnover.increaseBaseAmount20"
                                    v-bind:label="$t('baseIncrease20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.receiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.receiverTaxDebtorTurnover.increaseVat20"
                                    v-bind:label="$t('vatIncrease20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.receiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.receiverTaxDebtorTurnover.increaseBaseAmount10"
                                    v-bind:label="$t('baseIncrease10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.receiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.receiverTaxDebtorTurnover.increaseVat10"
                                    v-bind:label="$t('vatIncrease10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.receiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="12" style="font-weight:bold">                                   
                                    {{ $t('baseReduction')}}
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.receiverTaxDebtorTurnover.reductionBaseAmount20"
                                    v-bind:label="$t('baseReduction20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.receiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.receiverTaxDebtorTurnover.reductionVat20"
                                    v-bind:label="$t('vatReduction20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.receiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.receiverTaxDebtorTurnover.reductionBaseAmount10"
                                    v-bind:label="$t('baseReduction10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.receiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.receiverTaxDebtorTurnover.reductionVat10"
                                    v-bind:label="$t('vatReduction10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.receiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.receiverTaxDebtorTurnover.prepaymentReductionBaseAmount20"
                                    v-bind:label="$t('baseReductionPrepayment20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.receiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.receiverTaxDebtorTurnover.prepaymentReductionVat20"
                                    v-bind:label="$t('vatReductionPrepayment20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.receiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.receiverTaxDebtorTurnover.prepaymentReductionBaseAmount10"
                                    v-bind:label="$t('baseReductionPrepayment10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.receiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.receiverTaxDebtorTurnover.prepaymentReductionVat10"
                                    v-bind:label="$t('vatReductionPrepayment10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.receiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-content>
                </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
            </v-row>
            <v-row justify="space-around">
            <!-- StornoSupplierTaxDebtorTurnover -->
            <v-col cols="12">
                <v-expansion-panels
                v-model="stornoSupplierTaxDebtorTurnoverPanel">
                <v-expansion-panel>
                    <v-expansion-panel-header style="font-weight:bold">{{ $t('stornoSupplierTaxDebtorTurnover')}}</v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <v-row justify="space-around">
                                <v-col cols="12" style="font-weight:bold">                                   
                                    {{ $t('baseIncrease')}}
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoSupplierTaxDebtorTurnover.increaseBaseAmount20"
                                    v-bind:label="$t('baseIncrease20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoSupplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoSupplierTaxDebtorTurnover.increaseVat20"
                                    v-bind:label="$t('vatIncrease20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoSupplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoSupplierTaxDebtorTurnover.increaseBaseAmount10"
                                    v-bind:label="$t('baseIncrease10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoSupplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoSupplierTaxDebtorTurnover.increaseVat10"
                                    v-bind:label="$t('vatIncrease10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoSupplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="12" style="font-weight:bold">                                   
                                    {{ $t('baseReduction')}}
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoSupplierTaxDebtorTurnover.reductionBaseAmount20"
                                    v-bind:label="$t('baseReduction20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoSupplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoSupplierTaxDebtorTurnover.reductionVat20"
                                    v-bind:label="$t('vatReduction20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoSupplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoSupplierTaxDebtorTurnover.reductionBaseAmount10"
                                    v-bind:label="$t('baseReduction10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoSupplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoSupplierTaxDebtorTurnover.reductionVat10"
                                    v-bind:label="$t('vatReduction10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoSupplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoSupplierTaxDebtorTurnover.prepaymentReductionBaseAmount20"
                                    v-bind:label="$t('baseReductionPrepayment20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoSupplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoSupplierTaxDebtorTurnover.prepaymentReductionVat20"
                                    v-bind:label="$t('vatReductionPrepayment20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoSupplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoSupplierTaxDebtorTurnover.prepaymentReductionBaseAmount10"
                                    v-bind:label="$t('baseReductionPrepayment10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoSupplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoSupplierTaxDebtorTurnover.prepaymentReductionVat10"
                                    v-bind:label="$t('vatReductionPrepayment10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoSupplierTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-content>
                </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
            </v-row>
            <v-row justify="space-around">
            <!-- StornoReceiverTaxDebtorTurnover -->
            <v-col cols="12">
                <v-expansion-panels
                v-model="stornoReceiverTaxDebtorTurnoverPanel">
                <v-expansion-panel>
                    <v-expansion-panel-header style="font-weight:bold">{{ $t('stornoReceiverTaxDebtorTurnover')}}</v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <v-row justify="space-around">
                                <v-col cols="12" style="font-weight:bold">                                   
                                    {{ $t('baseIncrease')}}
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoReceiverTaxDebtorTurnover.increaseBaseAmount20"
                                    v-bind:label="$t('baseIncrease20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoReceiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoReceiverTaxDebtorTurnover.increaseVat20"
                                    v-bind:label="$t('vatIncrease20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoReceiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoReceiverTaxDebtorTurnover.increaseBaseAmount10"
                                    v-bind:label="$t('baseIncrease10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoReceiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoReceiverTaxDebtorTurnover.increaseVat10"
                                    v-bind:label="$t('vatIncrease10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoReceiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="12" style="font-weight:bold">                                   
                                    {{ $t('baseReduction')}}
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoReceiverTaxDebtorTurnover.reductionBaseAmount20"
                                    v-bind:label="$t('baseReduction20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoReceiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoReceiverTaxDebtorTurnover.reductionVat20"
                                    v-bind:label="$t('vatReduction20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoReceiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoReceiverTaxDebtorTurnover.reductionBaseAmount10"
                                    v-bind:label="$t('baseReduction10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoReceiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoReceiverTaxDebtorTurnover.reductionVat10"
                                    v-bind:label="$t('vatReduction10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoReceiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row justify="space-around">
                                <v-col cols="3" align-self="center">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoReceiverTaxDebtorTurnover.prepaymentReductionBaseAmount20"
                                    v-bind:label="$t('baseReductionPrepayment20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoReceiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoReceiverTaxDebtorTurnover.prepaymentReductionVat20"
                                    v-bind:label="$t('vatReductionPrepayment20')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoReceiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>                                
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoReceiverTaxDebtorTurnover.prepaymentReductionBaseAmount10"
                                    v-bind:label="$t('baseReductionPrepayment10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoReceiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="3">
                                    <v-text-field
                                    style="font-size:12px"
                                    :disabled="!(draft || correction)"
                                    v-model="vatRecord.stornoReceiverTaxDebtorTurnover.prepaymentReductionVat10"
                                    v-bind:label="$t('vatReductionPrepayment10')"
                                    :rules="[v => (!v || (/^(?!,$)[\d,.]+$/.test(v)) && parseFloat(v) >= 0) || $t('number') + ' >= 0']"
                                    @change="format(vatRecord.stornoReceiverTaxDebtorTurnover)"
                                    clearable>
                                    </v-text-field>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-content>
                </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
            </v-row>        
        <br /><br />
        <v-row class="footer"
        no-gutters
        >
            <v-btn
            class="mx-2"
            color="#2b5d66"
            :dark="hasChanged && !executingOperation"
            :disabled="!hasChanged || executingOperation"
            v-if="draft"
            @click="save">
            {{ $t('save') }}
            </v-btn>
            <v-btn
            class="mx-2"
            color="#2b5d66"
            :dark="hasChanged && !executingOperation"
            :disabled="!hasChanged || executingOperation"
            v-if="draft"
            @click="cancelChanges">
            {{ $t('discardChanges') }}
            </v-btn>
            <v-btn
            :dark="!executingOperation"
            :disabled="executingOperation"
            class="mx-2"
            color="#2b5d66"
            v-if="draft"
            @click="send">
            {{ $t('send') }}
            </v-btn>
            <v-btn
            class="mx-2"
            color="#2b5d66"
            :dark="!executingOperation"
            :disabled="executingOperation"
            v-if="draft"
            @click="deleteRecord">
            {{ $t('delete') }}
            </v-btn>
            <v-btn
            :dark="!executingOperation"
            :disabled="executingOperation"
            class="mx-2"
            color="#2b5d66"
            v-if="recorded && !correction"
            @click="correct">
            {{ $t('correct') }}
            </v-btn>
            <v-btn
            :dark="!executingOperation"
            :disabled="executingOperation"
            class="mx-2"
            color="#2b5d66"
            v-if="correction"
            @click="saveCorrection">
            {{ $t('saveCorrection') }}
            </v-btn>
            <v-btn
            :dark="!executingOperation"
            :disabled="executingOperation"
            class="mx-2"
            color="#2b5d66"
            v-if="correction"
            @click="cancelCorrection">
            {{ $t('cancelCorrection') }}
            </v-btn>
            <v-btn
            :dark="!executingOperation"
            :disabled="executingOperation"
            v-if="recorded"
            class="mx-2"
            color="#2b5d66"
            @click="annul">
            {{ $t('annul') }}
            </v-btn>
        </v-row>
    </v-form>
    <VersionDialog :versions="vatRecord.versions" v-if="showVersion" />
  </v-container>  
</template>
<script>
    import { i18n } from '../main'
    import VersionDialog from "../components/VatRecordVersionsDialog.vue"
    export default {
        name: "GroupVatForm",
        components: {
            VersionDialog
        },
        props:['id'],
        data() {
            return {                
                valid: true,
                correction: false,
                showVersion: false,
                foundNumber: false,
                prepaymentPanel: [0, 0],
                turnoverWithoutFeePanel: [0, 0],
                turnoverWithFeePanel: [0, 0],
                specialVatPanel: [0, 0],
                supplierTaxDebtorTurnoverPanel: [0, 0],
                receiverTaxDebtorTurnoverPanel: [0, 0],
                stornoSupplierTaxDebtorTurnoverPanel: [0, 0],
                stornoReceiverTaxDebtorTurnoverPanel: [0, 0],                
                initialVatRecord: {
                    groupVatRecordId: -1,
                    groupVatId: -1,
                    calculationNumber: "",
                    year: "",
                    turnoverDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10),
                    periodType: {
                        vatPeriodTypeId: 1
                    },
                    period: {
                        vatPeriodId: 1
                    },
                    status: {
                        vatRecordStatusId: 1,
                        vatRecordStatus: 'U pripremi',
                        vatRecordStatusEng: 'Draft'
                    },
                    prepaymentAmount20: null,
                    prepaymentBaseAmount20: null,
                    prepaymentCalculatedVat20: null,
                    prepaymentAmount10: null,
                    prepaymentBaseAmount10: null,
                    prepaymentCalculatedVat10: null,
                    baseAmount20: null,
                    calculatedVat20: null,
                    baseAmount10: null,
                    calculatedVat10: null,
                    noChargeBaseAmount20: null,
                    noChargeCalculatedVat20: null,
                    noChargeBaseAmount10: null,
                    noChargeCalculatedVat10: null,
                    touristServicesBaseAmount20: null,
                    touristServicesCalculatedVat20: null,
                    prepaymentTouristServicesBaseAmount20: null,
                    prepaymentTouristServicesCalculatedVat20: null,
                    artAntiquesCollectibleGoodsBaseAmount20: null,
                    artAntiquesCollectibleGoodsCalculatedVat20: null,
                    artAntiquesCollectibleGoodsBaseAmount10: null,
                    artAntiquesCollectibleGoodsCalculatedVat10: null,
                    prepaymentArtAntiquesCollectibleGoodsBaseAmount20: null,
                    prepaymentArtAntiquesCollectibleGoodsCalculatedVat20: null,
                    prepaymentArtAntiquesCollectibleGoodsBaseAmount10: null,
                    prepaymentArtAntiquesCollectibleGoodsCalculatedVat10: null,
                    supplierTaxDebtorTurnover: {
                        increaseBaseAmount20: null,
                        increaseVat20: null,
                        increaseBaseAmount10: null,
                        increaseVat10: null,
                        reductionBaseAmount20: null,
                        reductionVat20: null,
                        reductionBaseAmount10: null,
                        reductionVat10: null,
                        prepaymentReductionBaseAmount20: null,
                        prepaymentReductionVat20: null,
                        prepaymentReductionBaseAmount10: null,
                        prepaymentReductionVat10: null
                    },
                    receiverTaxDebtorTurnover: {
                        increaseBaseAmount20: null,
                        increaseVat20: null,
                        increaseBaseAmount10: null,
                        increaseVat10: null,
                        reductionBaseAmount20: null,
                        reductionVat20: null,
                        reductionBaseAmount10: null,
                        reductionVat10: null,
                        prepaymentReductionBaseAmount20: null,
                        prepaymentReductionVat20: null,
                        prepaymentReductionBaseAmount10: null,
                        prepaymentReductionVat10: null
                    },
                    stornoSupplierTaxDebtorTurnover: {
                        increaseBaseAmount20: null,
                        increaseVat20: null,
                        increaseBaseAmount10: null,
                        increaseVat10: null,
                        reductionBaseAmount20: null,
                        reductionVat20: null,
                        reductionBaseAmount10: null,
                        reductionVat10: null,
                        prepaymentReductionBaseAmount20: null,
                        prepaymentReductionVat20: null,
                        prepaymentReductionBaseAmount10: null,
                        prepaymentReductionVat10: null
                    },
                    stornoReceiverTaxDebtorTurnover: {
                        increaseBaseAmount20: null,
                        increaseVat20: null,
                        increaseBaseAmount10: null,
                        increaseVat10: null,
                        reductionBaseAmount20: null,
                        reductionVat20: null,
                        reductionBaseAmount10: null,
                        reductionVat10: null,
                        prepaymentReductionBaseAmount20: null,
                        prepaymentReductionVat20: null,
                        prepaymentReductionBaseAmount10: null,
                        prepaymentReductionVat10: null
                    },
                    vatReductionFromPreviousPeriodAmount: this.formatNumberToString(0),
                    vatIncreaseFromPreviousPeriodAmount: this.formatNumberToString(0),
                    turnoverWithFee: {
                        turnoverDescription20: "",
                        turnoverDescription10: "",
                        taxableAmount20: this.formatNumberToString(0),
                        taxableAmount10: this.formatNumberToString(0),
                        taxAmount20: this.formatNumberToString(0),
                        taxAmount10: this.formatNumberToString(0),
                        totalAmount20: this.formatNumberToString(0),
                        totalAmount10: this.formatNumberToString(0)
                    },
                    turnoverWithoutFee: {
                        turnoverDescription20: "",
                        turnoverDescription10: "",
                        taxableAmount20: this.formatNumberToString(0),
                        taxableAmount10: this.formatNumberToString(0),
                        taxAmount20: this.formatNumberToString(0),
                        taxAmount10: this.formatNumberToString(0),
                        totalAmount20: this.formatNumberToString(0),
                        totalAmount10: this.formatNumberToString(0)
                    },
                    futureTurnover: {
                        turnoverDescription20: "",
                        turnoverDescription10: "",
                        taxableAmount20: this.formatNumberToString(0),
                        taxableAmount10: this.formatNumberToString(0),
                        taxAmount20: this.formatNumberToString(0),
                        taxAmount10: this.formatNumberToString(0),
                        totalAmount20: this.formatNumberToString(0),
                        totalAmount10: this.formatNumberToString(0)
                    },
                    versions: []
                },
                vatRecord: {
                    groupVatRecordId: -1,
                    groupVatId: -1,
                    calculationNumber: "",
                    year: "",
                    turnoverDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10),
                    periodType: {
                        vatPeriodTypeId: 1
                    },
                    period: {
                        vatPeriodId: 1
                    },
                    status: {
                        vatRecordStatusId: 1,
                        vatRecordStatus: 'U pripremi',
                        vatRecordStatusEng: 'Draft'
                    },
                    prepaymentAmount20: null,
                    prepaymentBaseAmount20: null,
                    prepaymentCalculatedVat20: null,
                    prepaymentAmount10: null,
                    prepaymentBaseAmount10: null,
                    prepaymentCalculatedVat10: null,
                    baseAmount20: null,
                    calculatedVat20: null,
                    baseAmount10: null,
                    calculatedVat10: null,
                    noChargeBaseAmount20: null,
                    noChargeCalculatedVat20: null,
                    noChargeBaseAmount10: null,
                    noChargeCalculatedVat10: null,
                    touristServicesBaseAmount20: null,
                    touristServicesCalculatedVat20: null,
                    prepaymentTouristServicesBaseAmount20: null,
                    prepaymentTouristServicesCalculatedVat20: null,
                    artAntiquesCollectibleGoodsBaseAmount20: null,
                    artAntiquesCollectibleGoodsCalculatedVat20: null,
                    artAntiquesCollectibleGoodsBaseAmount10: null,
                    artAntiquesCollectibleGoodsCalculatedVat10: null,
                    prepaymentArtAntiquesCollectibleGoodsBaseAmount20: null,
                    prepaymentArtAntiquesCollectibleGoodsCalculatedVat20: null,
                    prepaymentArtAntiquesCollectibleGoodsBaseAmount10: null,
                    prepaymentArtAntiquesCollectibleGoodsCalculatedVat10: null,
                    supplierTaxDebtorTurnover: {
                        increaseBaseAmount20: null,
                        increaseVat20: null,
                        increaseBaseAmount10: null,
                        increaseVat10: null,
                        reductionBaseAmount20: null,
                        reductionVat20: null,
                        reductionBaseAmount10: null,
                        reductionVat10: null,
                        prepaymentReductionBaseAmount20: null,
                        prepaymentReductionVat20: null,
                        prepaymentReductionBaseAmount10: null,
                        prepaymentReductionVat10: null
                    },
                    receiverTaxDebtorTurnover: {
                        increaseBaseAmount20: null,
                        increaseVat20: null,
                        increaseBaseAmount10: null,
                        increaseVat10: null,
                        reductionBaseAmount20: null,
                        reductionVat20: null,
                        reductionBaseAmount10: null,
                        reductionVat10: null,
                        prepaymentReductionBaseAmount20: null,
                        prepaymentReductionVat20: null,
                        prepaymentReductionBaseAmount10: null,
                        prepaymentReductionVat10: null
                    },
                    stornoSupplierTaxDebtorTurnover: {
                        increaseBaseAmount20: null,
                        increaseVat20: null,
                        increaseBaseAmount10: null,
                        increaseVat10: null,
                        reductionBaseAmount20: null,
                        reductionVat20: null,
                        reductionBaseAmount10: null,
                        reductionVat10: null,
                        prepaymentReductionBaseAmount20: null,
                        prepaymentReductionVat20: null,
                        prepaymentReductionBaseAmount10: null,
                        prepaymentReductionVat10: null
                    },
                    stornoReceiverTaxDebtorTurnover: {
                        increaseBaseAmount20: null,
                        increaseVat20: null,
                        increaseBaseAmount10: null,
                        increaseVat10: null,
                        reductionBaseAmount20: null,
                        reductionVat20: null,
                        reductionBaseAmount10: null,
                        reductionVat10: null,
                        prepaymentReductionBaseAmount20: null,
                        prepaymentReductionVat20: null,
                        prepaymentReductionBaseAmount10: null,
                        prepaymentReductionVat10: null
                    },
                    vatReductionFromPreviousPeriodAmount: this.formatNumberToString(0),
                    vatIncreaseFromPreviousPeriodAmount: this.formatNumberToString(0),
                    turnoverWithFee: {
                        turnoverDescription20: "",
                        turnoverDescription10: "",
                        taxableAmount20: this.formatNumberToString(0),
                        taxableAmount10: this.formatNumberToString(0),
                        taxAmount20: this.formatNumberToString(0),
                        taxAmount10: this.formatNumberToString(0),
                        totalAmount20: this.formatNumberToString(0),
                        totalAmount10: this.formatNumberToString(0)
                    },
                    turnoverWithoutFee: {
                        turnoverDescription20: "",
                        turnoverDescription10: "",
                        taxableAmount20: this.formatNumberToString(0),
                        taxableAmount10: this.formatNumberToString(0),
                        taxAmount20: this.formatNumberToString(0),
                        taxAmount10: this.formatNumberToString(0),
                        totalAmount20: this.formatNumberToString(0),
                        totalAmount10: this.formatNumberToString(0)
                    },
                    futureTurnover: {
                        turnoverDescription20: "",
                        turnoverDescription10: "",
                        taxableAmount20: this.formatNumberToString(0),
                        taxableAmount10: this.formatNumberToString(0),
                        taxAmount20: this.formatNumberToString(0),
                        taxAmount10: this.formatNumberToString(0),
                        totalAmount20: this.formatNumberToString(0),
                        totalAmount10: this.formatNumberToString(0)
                    },
                    versions: []
                },
                menuTurnoverDate: false,
                vatPeriodTypeItems: [],
                vatPeriodAllItems: [],
                vatPeriodItems:[],
                executingOperation: false
            }
        },
        async beforeRouteLeave (to, from, next) {
            if (this.hasChanged && to.name != 'Login' && this.vatRecord.status.vatRecordStatusId == 1 && !await this.$confirm(this.$t('hasChanges'), { title: this.$t('warning'), buttonTrueText: this.$t('yes'), buttonFalseText: this.$t('no'), persistent: true })){
                next(false)
            } else {
                next()
            }
        },
        created() {            
            this.init();
            window.addEventListener('beforeunload', this.beforeWindowUnload);
        },       
        computed: {
            hasChanged() {
                return JSON.stringify(this.vatRecord) !== JSON.stringify(this.initialVatRecord)
            },
            draft() {
                return this.vatRecord.status.vatRecordStatusId == 1;
            },
            recorded() {
                return this.vatRecord.status.vatRecordStatusId == 2;
            },
            selectedOrganization() {
                return this.$store.getters.organizationId;
            },
        },
        beforeDestroy() {
            window.removeEventListener('beforeunload', this.beforeWindowUnload)
        },
        methods: {
            async init() {
                await this.vatRecordData();
                await this.getVatPeriodType();
                await this.getVatPeriod();
                if(this.vatRecord.groupVatRecordId <= 0) {
                    this.vatRecord.period = this.vatPeriodItems[0];
                }
            },
            async getVatPeriodType() {
                await this.axios.get('InvoiceUtilities/GetVatPeriodType').then((response) => {
                    this.vatPeriodTypeItems = response.data;
                })
                .catch(err => console.log(err));
            },
            async getVatPeriod() {
                await this.axios.get('InvoiceUtilities/GetVatPeriod').then((response) => {
                    this.vatPeriodAllItems = response.data;
                    this.vatPeriodItems = this.vatPeriodAllItems.filter(p => { return p.vatPeriodTypeId === this.vatRecord.periodType.vatPeriodTypeId});
                    
                })
                .catch(err => console.log(err));
            },
            periodTypeChanged() {
                this.vatPeriodItems = this.vatPeriodAllItems.filter(p => { return p.vatPeriodTypeId === this.vatRecord.periodType.vatPeriodTypeId});
                this.vatRecord.period = this.vatPeriodItems[0];
            },
            async vatRecordData() {
                if(this.$route.query.id != undefined) {
                    await this.axios.get('VatRecord/GetGroupVatRecordDetails?groupVatRecordId=' + this.$route.query.id).then((response) => {
                        this.vatRecord = response.data;
                        this.formatAllNumbersToString();

                        if(this.vatRecord.status.vatRecordStatusId == 1) {
                            this.vatRecord.turnoverDate = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10);
                        }

                        this.initialVatRecord = JSON.parse(JSON.stringify(this.vatRecord));                        
                    })
                    .catch(err => console.log(err));
                }
            },
            documentNumberRule() {
                const rules = [v => !!v || this.$t('calculationNumberRequired')]
                if(this.foundNumber) {
                    const rule = this.$t('invoiceNumberExist')
                    rules.push(rule)
                }
                return rules;
            },
            checkDocumentNumber() {
                if(this.vatRecord.calculationNumber.length > 1)
                    this.axios.get('VatRecord/CheckGroupVatNumber?organizationId=' + this.$store.getters.organizationId + '&id=' + this.vatRecord.groupVatRecordId + '&number=' + this.vatRecord.calculationNumber).then((response) => {
                        this.foundNumber = response.data
                    })
                    .catch(err => console.log(err))
            },
            save() {
                this.executingOperation = true;
                if(this.$refs.vatRecordForm.validate()) {
                    var formData = new FormData();
                    console.log(this.vatRecord)
                    formData.append('recordJson', JSON.stringify(this.vatRecord));
                    this.axios.post('VatRecord/SaveGroupVatRecord?organizationId=' + this.selectedOrganization, formData, {
                        headers: {
                            "Content-Type": "multipart/form-data"
                        }
                        }).then(response => {  
                        if(response.data.message != null && response.data.groupVatRecordId <= 0) {
                            this.$root.displayNotificationMsg(response.data.message, 'warning')
                            this.executingOperation = false;
                            return
                        }                       
                        else if(response && response.status === 200) {
                            this.$root.displayNotificationMsg(this.$t('groupVatRecordSavedMessage', {number: this.vatRecord.calculationNumber}), 'success');
                            this.initialVatRecord = JSON.parse(JSON.stringify(this.vatRecord));
                            this.$router.push({ name: 'GroupVatRecord'});
                        }
                    })
                    .catch((error) => {
                        if (error.response) {
                            console.log(error.response.data.detail);
                            console.log(error.response.status);
                            this.$root.displayNotificationMsg(error.response.data.detail, 'warning')
                        }
                        else if (error.request) {
                            console.log(error.request);
                        } 
                    })
                }
                else {
                    this.$root.displayNotificationMsg(this.$t('dataRequired'), 'warning');
                    this.executingOperation = false;
                }
            },
            async cancelChanges() {
                if(this.hasChanged && await this.$confirm(this.$t('cancelChangesMessage'), { title: this.$t('warning'), buttonTrueText: this.$t('yes'), buttonFalseText: this.$t('no'), persistent: true }))
                    this.vatRecord = JSON.parse(JSON.stringify(this.initialVatRecord))
            },
            send() {
                this.executingOperation = true;
                if(this.$refs.vatRecordForm.validate()) {
                    var formData = new FormData();
                    formData.append('recordJson', JSON.stringify(this.vatRecord));
                    this.axios.post('VatRecord/SendGroupVatRecordWithFormattedValues?organizationId=' + this.selectedOrganization + '&hasChanged=' + this.hasChanged, formData, {
                        headers: {
                            "Content-Type": "multipart/form-data"
                        }
                        }).then(response => { 
                        if(response.data.message != null && response.data.groupVatId <= 0) {
                            this.$root.displayNotificationMsg(response.data.message, 'warning')
                            this.executingOperation = false;
                            return
                        }  
                        else if(response && response.status === 200) {
                            this.vatRecord = response.data;
                            this.initialVatRecord = JSON.parse(JSON.stringify(this.vatRecord));
                            this.$root.displayNotificationMsg(this.$t('groupVatRecordSendMessage', {number: this.vatRecord.calculationNumber}), 'success');
                            
                            this.$router.push({ name: 'GroupVatRecord'});
                        }
                    })
                    .catch((error) => {
                        console.log(error)
                        if (error.response) {
                        console.log(error.response.data);
                        console.log(error.response.status);
                        console.log(error.response.headers);
                        this.$root.displayNotificationMsg(error.response.data, 'warning')
                        }
                        else if (error.request) {
                        console.log(error.request);
                        } 
                    })
                }                
                else {
                    this.$root.displayNotificationMsg(this.$t('dataRequired'), 'warning');
                    this.executingOperation = false;
                }
            },
            correct() {
                this.correction = true;     
                this.vatRecord.turnoverDate = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10);           
            },
            cancelCorrection() {
                this.correction = false; 
                this.vatRecord.turnoverDate = this.initialVatRecord.turnoverDate;
            },
            saveCorrection() {
                this.executingOperation = true;
                this.vatRecord.groupVatRecordId = -1;
                var formData = new FormData();
                formData.append('recordJson', JSON.stringify(this.vatRecord));
                this.axios.post('VatRecord/CorrectGroupVatRecordWithFormattedValues?organizationId=' + this.selectedOrganization + '&hasChanged=' + this.hasChanged, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                    }).then(response => { 
                    if(response.data.message != null && response.data.groupVatId <= 0) {
                        this.$root.displayNotificationMsg(response.data.message, 'warning')
                        this.executingOperation = false;
                        return
                    }  
                    else if(response && response.status === 200) {
                        this.correction = false;
                        this.vatRecord = response.data;
                        this.initialVatRecord = JSON.parse(JSON.stringify(this.vatRecord));
                        this.$root.displayNotificationMsg(this.$t('groupVatRecordCorrectionMessage', {number: this.vatRecord.calculationNumber}), 'success');
                        
                        this.$router.push({ name: 'GroupVatRecord'});
                    }
                })
                .catch((error) => {
                    console.log(error)
                    if (error.response) {
                        console.log(error.response.data);
                        console.log(error.response.status);
                        console.log(error.response.headers);
                        this.$root.displayNotificationMsg(error.response.data, 'warning')
                    }
                    else if (error.request) {
                        console.log(error.request);
                    } 
                })
            },
            async annul() {
                this.executingOperation = true;
                if(await this.$confirm(this.$t('cancelVatRecordMessage', {number: this.vatRecord.calculationNumber}), { title: this.$t('warning'), buttonTrueText: this.$t('yes'), buttonFalseText: this.$t('no'), persistent: true })) {
                    this.axios.post('VatRecord/CancelGroupVatRecord?organizationId=' + this.selectedOrganization + '&id=' + this.vatRecord.groupVatId).then((response) => {
                      if(response && response.status === 200) {
                        this.$root.displayNotificationMsg(this.$t('groupVatRecordCancelMessage', {number: this.vatRecord.calculationNumber}), 'success');
                        this.$router.push({ name: 'GroupVatRecord'});
                      }                      
                })
                .catch((error) => {
                    console.log(error);
                    this.$root.displayNotificationMsg(error.response.data, 'warning');
                    })
                }
            },
            async deleteRecord() {
                this.executingOperation = true;
                if(await this.$confirm(this.$t('cancelGroupVatRecordMessage', {number: this.vatRecord.calculationNumber}), { title: this.$t('warning'), buttonTrueText: this.$t('yes'), buttonFalseText: this.$t('no'), persistent: true })) {
                    this.axios.delete('VatRecord/deleteGroupVatRecord/' + this.vatRecord.groupVatRecordId).then((response) => {
                      if(response && response.status === 200) {
                        this.$root.displayNotificationMsg(this.$t('groupVatRecordDeleteMessage', {number: this.vatRecord.calculationNumber}), 'success');
                        this.$router.push({ name: 'GroupVatRecord'});
                      }                      
                })
                .catch((error) => {
                    console.log(error);
                    this.$root.displayNotificationMsg(error.response.data, 'warning');
                    this.executingOperation = false;
                    })
                }                
                else {
                    this.executingOperation = false;
                }
            },
            calculateTotal() {
                if(this.vatRecord.prepaymentAmount20 && this.vatRecord.prepaymentAmount20.length > 0) {
                    this.vatRecord.prepaymentBaseAmount20 = parseFloat(this.convertFormattedStringToNumber(this.vatRecord.prepaymentAmount20)) / 1.2;
                    this.vatRecord.prepaymentCalculatedVat20 = this.vatRecord.prepaymentBaseAmount20 * 0.2;
                    this.vatRecord.prepaymentBaseAmount20 = this.formatNumberToString(this.vatRecord.prepaymentBaseAmount20);
                    this.vatRecord.prepaymentCalculatedVat20 = this.formatNumberToString(this.vatRecord.prepaymentCalculatedVat20);
                }

                if(this.vatRecord.prepaymentAmount10 && this.vatRecord.prepaymentAmount10.length > 0) {
                    this.vatRecord.prepaymentBaseAmount10 = parseFloat(this.convertFormattedStringToNumber(this.vatRecord.prepaymentAmount10)) / 1.1;
                    this.vatRecord.prepaymentCalculatedVat10 = this.vatRecord.prepaymentBaseAmount10 * 0.1;
                    this.vatRecord.prepaymentBaseAmount10 = this.formatNumberToString(this.vatRecord.prepaymentBaseAmount10);
                    this.vatRecord.prepaymentCalculatedVat10 = this.formatNumberToString(this.vatRecord.prepaymentCalculatedVat10);
                }
            },
            formatNumberToString(value) {
                value = value.toString().replace(",", ".");
                var formatter = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2});
                return formatter.format(value);
            },
            convertFormattedStringToNumber(value) {
                while (value.includes(".")) {

                    value = value.replace(".", "")
                }
                value = value.replace(",", ".")

                return value
            },
            formatAllNumbersToString() {
                if(this.vatRecord.turnoverWithFee.taxableAmount20)
                this.vatRecord.turnoverWithFee.taxableAmount20 = this.formatNumberToString(this.vatRecord.turnoverWithFee.taxableAmount20);
                if(this.vatRecord.turnoverWithFee.taxableAmount10)
                    this.vatRecord.turnoverWithFee.taxableAmount10 = this.formatNumberToString(this.vatRecord.turnoverWithFee.taxableAmount10);
                if(this.vatRecord.turnoverWithFee.taxAmount20)
                    this.vatRecord.turnoverWithFee.taxAmount20 = this.formatNumberToString(this.vatRecord.turnoverWithFee.taxAmount20);
                if(this.vatRecord.turnoverWithFee.taxAmount10)
                    this.vatRecord.turnoverWithFee.taxAmount10 = this.formatNumberToString(this.vatRecord.turnoverWithFee.taxAmount10);
                if(this.vatRecord.turnoverWithFee.totalAmount20)
                    this.vatRecord.turnoverWithFee.totalAmount20 = this.formatNumberToString(this.vatRecord.turnoverWithFee.totalAmount20);
                if(this.vatRecord.turnoverWithFee.totalAmount10)
                    this.vatRecord.turnoverWithFee.totalAmount10 = this.formatNumberToString(this.vatRecord.turnoverWithFee.totalAmount10);

                if(this.vatRecord.turnoverWithoutFee.taxableAmount20)
                    this.vatRecord.turnoverWithoutFee.taxableAmount20 = this.formatNumberToString(this.vatRecord.turnoverWithoutFee.taxableAmount20);
                if(this.vatRecord.turnoverWithoutFee.taxableAmount10)
                    this.vatRecord.turnoverWithoutFee.taxableAmount10 = this.formatNumberToString(this.vatRecord.turnoverWithoutFee.taxableAmount10);
                if(this.vatRecord.turnoverWithoutFee.taxAmount20)
                    this.vatRecord.turnoverWithoutFee.taxAmount20 = this.formatNumberToString(this.vatRecord.turnoverWithoutFee.taxAmount20);
                if(this.vatRecord.turnoverWithoutFee.taxAmount10)
                    this.vatRecord.turnoverWithoutFee.taxAmount10 = this.formatNumberToString(this.vatRecord.turnoverWithoutFee.taxAmount10);
                //this.vatRecord.turnoverWithoutFee.totalAmount20 = this.formatNumberToString(this.vatRecord.turnoverWithoutFee.totalAmount20);
                //this.vatRecord.turnoverWithoutFee.totalAmount10 = this.formatNumberToString(this.vatRecord.turnoverWithoutFee.totalAmount10);
                if(this.vatRecord.futureTurnover.taxableAmount20)
                    this.vatRecord.futureTurnover.taxableAmount20 = this.formatNumberToString(this.vatRecord.futureTurnover.taxableAmount20);
                if(this.vatRecord.futureTurnover.taxableAmount10)
                    this.vatRecord.futureTurnover.taxableAmount10 = this.formatNumberToString(this.vatRecord.futureTurnover.taxableAmount10);
                if(this.vatRecord.futureTurnover.taxAmount20)
                    this.vatRecord.futureTurnover.taxAmount20 = this.formatNumberToString(this.vatRecord.futureTurnover.taxAmount20);
                if(this.vatRecord.futureTurnover.taxAmount10)
                    this.vatRecord.futureTurnover.taxAmount10 = this.formatNumberToString(this.vatRecord.futureTurnover.taxAmount10);
                //this.vatRecord.futureTurnover.totalAmount20 = this.formatNumberToString(this.vatRecord.futureTurnover.totalAmount20);
                //this.vatRecord.futureTurnover.totalAmount10 = this.formatNumberToString(this.vatRecord.futureTurnover.totalAmount10);

                if(this.vatRecord.vatReductionFromPreviousPeriodAmoun)
                    this.vatRecord.vatReductionFromPreviousPeriodAmount = this.formatNumberToString(this.vatRecord.vatReductionFromPreviousPeriodAmount);
                
                if(this.vatRecord.vatIncreaseFromPreviousPeriodAmount)
                    this.vatRecord.vatIncreaseFromPreviousPeriodAmount = this.formatNumberToString(this.vatRecord.vatIncreaseFromPreviousPeriodAmount);

                if(this.vatRecord.prepaymentAmount20)
                    this.vatRecord.prepaymentAmount20 = this.formatNumberToString(this.vatRecord.prepaymentAmount20);
                if(this.vatRecord.prepaymentBaseAmount20)
                    this.vatRecord.prepaymentBaseAmount20 = this.formatNumberToString(this.vatRecord.prepaymentBaseAmount20);
                if(this.vatRecord.prepaymentCalculatedVat20)
                    this.vatRecord.prepaymentCalculatedVat20 = this.formatNumberToString(this.vatRecord.prepaymentCalculatedVat20);
                if(this.vatRecord.prepaymentAmount10)
                    this.vatRecord.prepaymentAmount10 = this.formatNumberToString(this.vatRecord.prepaymentAmount10);
                if(this.vatRecord.prepaymentBaseAmount10)
                    this.vatRecord.prepaymentBaseAmount10 = this.formatNumberToString(this.vatRecord.prepaymentBaseAmount10);
                if(this.vatRecord.prepaymentCalculatedVat10)
                    this.vatRecord.prepaymentCalculatedVat10 = this.formatNumberToString(this.vatRecord.prepaymentCalculatedVat10);
                if(this.vatRecord.baseAmount20)
                    this.vatRecord.baseAmount20 = this.formatNumberToString(this.vatRecord.baseAmount20);
                if(this.vatRecord.calculatedVat20)
                    this.vatRecord.calculatedVat20 = this.formatNumberToString(this.vatRecord.calculatedVat20);
                if(this.vatRecord.baseAmount10)
                    this.vatRecord.baseAmount10 = this.formatNumberToString(this.vatRecord.baseAmount10);
                if(this.vatRecord.calculatedVat10)
                    this.vatRecord.calculatedVat10 = this.formatNumberToString(this.vatRecord.calculatedVat10);
                if(this.vatRecord.noChargeBaseAmount20)
                    this.vatRecord.noChargeBaseAmount20 = this.formatNumberToString(this.vatRecord.noChargeBaseAmount20);
                if(this.vatRecord.noChargeCalculatedVat20)
                    this.vatRecord.noChargeCalculatedVat20 = this.formatNumberToString(this.vatRecord.noChargeCalculatedVat20);
                if(this.vatRecord.noChargeBaseAmount10)
                    this.vatRecord.noChargeBaseAmount10 = this.formatNumberToString(this.vatRecord.noChargeBaseAmount10);
                if(this.vatRecord.noChargeCalculatedVat10)
                    this.vatRecord.noChargeCalculatedVat10 = this.formatNumberToString(this.vatRecord.noChargeCalculatedVat10);
                if(this.vatRecord.touristServicesBaseAmount20)
                    this.vatRecord.touristServicesBaseAmount20 = this.formatNumberToString(this.vatRecord.touristServicesBaseAmount20);
                if(this.vatRecord.touristServicesCalculatedVat20)
                    this.vatRecord.touristServicesCalculatedVat20 = this.formatNumberToString(this.vatRecord.touristServicesCalculatedVat20);
                if(this.vatRecord.prepaymentTouristServicesBaseAmount20)
                    this.vatRecord.prepaymentTouristServicesBaseAmount20 = this.formatNumberToString(this.vatRecord.prepaymentTouristServicesBaseAmount20);
                if(this.vatRecord.prepaymentTouristServicesCalculatedVat20)
                    this.vatRecord.prepaymentTouristServicesCalculatedVat20 = this.formatNumberToString(this.vatRecord.prepaymentTouristServicesCalculatedVat20);
                if(this.vatRecord.artAntiquesCollectibleGoodsBaseAmount20)
                    this.vatRecord.artAntiquesCollectibleGoodsBaseAmount20 = this.formatNumberToString(this.vatRecord.artAntiquesCollectibleGoodsBaseAmount20);
                if(this.vatRecord.artAntiquesCollectibleGoodsCalculatedVat20)
                    this.vatRecord.artAntiquesCollectibleGoodsCalculatedVat20 = this.formatNumberToString(this.vatRecord.artAntiquesCollectibleGoodsCalculatedVat20);
                if(this.vatRecord.artAntiquesCollectibleGoodsBaseAmount10)
                    this.vatRecord.artAntiquesCollectibleGoodsBaseAmount10 = this.formatNumberToString(this.vatRecord.artAntiquesCollectibleGoodsBaseAmount10);
                if(this.vatRecord.artAntiquesCollectibleGoodsCalculatedVat10)
                    this.vatRecord.artAntiquesCollectibleGoodsCalculatedVat10 = this.formatNumberToString(this.vatRecord.artAntiquesCollectibleGoodsCalculatedVat10);
                if(this.vatRecord.prepaymentArtAntiquesCollectibleGoodsBaseAmount20)
                    this.vatRecord.prepaymentArtAntiquesCollectibleGoodsBaseAmount20 = this.formatNumberToString(this.vatRecord.prepaymentArtAntiquesCollectibleGoodsBaseAmount20);
                if(this.vatRecord.prepaymentArtAntiquesCollectibleGoodsCalculatedVat20)
                    this.vatRecord.prepaymentArtAntiquesCollectibleGoodsCalculatedVat20 = this.formatNumberToString(this.vatRecord.prepaymentArtAntiquesCollectibleGoodsCalculatedVat20);
                if(this.vatRecord.prepaymentArtAntiquesCollectibleGoodsBaseAmount10)
                    this.vatRecord.prepaymentArtAntiquesCollectibleGoodsBaseAmount10 = this.formatNumberToString(this.vatRecord.prepaymentArtAntiquesCollectibleGoodsBaseAmount10);
                if(this.vatRecord.prepaymentArtAntiquesCollectibleGoodsCalculatedVat10)
                    this.vatRecord.prepaymentArtAntiquesCollectibleGoodsCalculatedVat10 = this.formatNumberToString(this.vatRecord.prepaymentArtAntiquesCollectibleGoodsCalculatedVat10);
                if(this.vatRecord.touristServicesBaseAmount20)
                    this.vatRecord.touristServicesBaseAmount20 = this.formatNumberToString(this.vatRecord.touristServicesBaseAmount20);
                
                if(this.vatRecord.supplierTaxDebtorTurnover.increaseBaseAmount20)
                    this.vatRecord.supplierTaxDebtorTurnover.increaseBaseAmount20 = this.formatNumberToString(this.vatRecord.supplierTaxDebtorTurnover.increaseBaseAmount20);
                if(this.vatRecord.supplierTaxDebtorTurnover.increaseVat20)
                    this.vatRecord.supplierTaxDebtorTurnover.increaseVat20 = this.formatNumberToString(this.vatRecord.supplierTaxDebtorTurnover.increaseVat20);
                if(this.vatRecord.supplierTaxDebtorTurnover.increaseBaseAmount10)
                    this.vatRecord.supplierTaxDebtorTurnover.increaseBaseAmount10 = this.formatNumberToString(this.vatRecord.supplierTaxDebtorTurnover.increaseBaseAmount10);
                if(this.vatRecord.supplierTaxDebtorTurnover.increaseVat10)
                    this.vatRecord.supplierTaxDebtorTurnover.increaseVat10 = this.formatNumberToString(this.vatRecord.supplierTaxDebtorTurnover.increaseVat10);
                if(this.vatRecord.supplierTaxDebtorTurnover.reductionBaseAmount20)
                    this.vatRecord.supplierTaxDebtorTurnover.reductionBaseAmount20 = this.formatNumberToString(this.vatRecord.supplierTaxDebtorTurnover.reductionBaseAmount20);
                if(this.vatRecord.supplierTaxDebtorTurnover.reductionVat20)
                    this.vatRecord.supplierTaxDebtorTurnover.reductionVat20 = this.formatNumberToString(this.vatRecord.supplierTaxDebtorTurnover.reductionVat20);
                if(this.vatRecord.supplierTaxDebtorTurnover.reductionBaseAmount10)
                    this.vatRecord.supplierTaxDebtorTurnover.reductionBaseAmount10 = this.formatNumberToString(this.vatRecord.supplierTaxDebtorTurnover.reductionBaseAmount10);
                if(this.vatRecord.supplierTaxDebtorTurnover.reductionVat10)
                    this.vatRecord.supplierTaxDebtorTurnover.reductionVat10 = this.formatNumberToString(this.vatRecord.supplierTaxDebtorTurnover.reductionVat10);
                if(this.vatRecord.supplierTaxDebtorTurnover.prepaymentReductionBaseAmount20)
                    this.vatRecord.supplierTaxDebtorTurnover.prepaymentReductionBaseAmount20 = this.formatNumberToString(this.vatRecord.supplierTaxDebtorTurnover.prepaymentReductionBaseAmount20);
                if(this.vatRecord.supplierTaxDebtorTurnover.prepaymentReductionVat20)
                    this.vatRecord.supplierTaxDebtorTurnover.prepaymentReductionVat20 = this.formatNumberToString(this.vatRecord.supplierTaxDebtorTurnover.prepaymentReductionVat20);
                if(this.vatRecord.supplierTaxDebtorTurnover.prepaymentReductionBaseAmount10)
                    this.vatRecord.supplierTaxDebtorTurnover.prepaymentReductionBaseAmount10 = this.formatNumberToString(this.vatRecord.supplierTaxDebtorTurnover.prepaymentReductionBaseAmount10);
                if(this.vatRecord.supplierTaxDebtorTurnover.prepaymentReductionVat10)
                    this.vatRecord.supplierTaxDebtorTurnover.prepaymentReductionVat10 = this.formatNumberToString(this.vatRecord.supplierTaxDebtorTurnover.prepaymentReductionVat10);

                if(this.vatRecord.receiverTaxDebtorTurnover.increaseBaseAmount20)
                    this.vatRecord.receiverTaxDebtorTurnover.increaseBaseAmount20 = this.formatNumberToString(this.vatRecord.receiverTaxDebtorTurnover.increaseBaseAmount20);
                if(this.vatRecord.receiverTaxDebtorTurnover.increaseVat20)
                    this.vatRecord.receiverTaxDebtorTurnover.increaseVat20 = this.formatNumberToString(this.vatRecord.receiverTaxDebtorTurnover.increaseVat20);
                if(this.vatRecord.receiverTaxDebtorTurnover.increaseBaseAmount10)
                    this.vatRecord.receiverTaxDebtorTurnover.increaseBaseAmount10 = this.formatNumberToString(this.vatRecord.receiverTaxDebtorTurnover.increaseBaseAmount10);
                if(this.vatRecord.receiverTaxDebtorTurnover.increaseVat10)
                    this.vatRecord.receiverTaxDebtorTurnover.increaseVat10 = this.formatNumberToString(this.vatRecord.receiverTaxDebtorTurnover.increaseVat10);
                if(this.vatRecord.receiverTaxDebtorTurnover.reductionBaseAmount20)
                    this.vatRecord.receiverTaxDebtorTurnover.reductionBaseAmount20 = this.formatNumberToString(this.vatRecord.receiverTaxDebtorTurnover.reductionBaseAmount20);
                if(this.vatRecord.receiverTaxDebtorTurnover.reductionVat20)
                    this.vatRecord.receiverTaxDebtorTurnover.reductionVat20 = this.formatNumberToString(this.vatRecord.receiverTaxDebtorTurnover.reductionVat20);
                if(this.vatRecord.receiverTaxDebtorTurnover.reductionBaseAmount10)
                    this.vatRecord.receiverTaxDebtorTurnover.reductionBaseAmount10 = this.formatNumberToString(this.vatRecord.receiverTaxDebtorTurnover.reductionBaseAmount10);
                if(this.vatRecord.receiverTaxDebtorTurnover.reductionVat10)
                    this.vatRecord.receiverTaxDebtorTurnover.reductionVat10 = this.formatNumberToString(this.vatRecord.receiverTaxDebtorTurnover.reductionVat10);
                if(this.vatRecord.receiverTaxDebtorTurnover.prepaymentReductionBaseAmount20)
                    this.vatRecord.receiverTaxDebtorTurnover.prepaymentReductionBaseAmount20 = this.formatNumberToString(this.vatRecord.receiverTaxDebtorTurnover.prepaymentReductionBaseAmount20);
                if(this.vatRecord.receiverTaxDebtorTurnover.prepaymentReductionVat20)
                    this.vatRecord.receiverTaxDebtorTurnover.prepaymentReductionVat20 = this.formatNumberToString(this.vatRecord.receiverTaxDebtorTurnover.prepaymentReductionVat20);
                if(this.vatRecord.receiverTaxDebtorTurnover.prepaymentReductionBaseAmount10)
                    this.vatRecord.receiverTaxDebtorTurnover.prepaymentReductionBaseAmount10 = this.formatNumberToString(this.vatRecord.receiverTaxDebtorTurnover.prepaymentReductionBaseAmount10);
                if(this.vatRecord.receiverTaxDebtorTurnover.prepaymentReductionVat10)
                    this.vatRecord.receiverTaxDebtorTurnover.prepaymentReductionVat10 = this.formatNumberToString(this.vatRecord.receiverTaxDebtorTurnover.prepaymentReductionVat10);

                if(this.vatRecord.stornoSupplierTaxDebtorTurnover.increaseBaseAmount20)
                    this.vatRecord.stornoSupplierTaxDebtorTurnover.increaseBaseAmount20 = this.formatNumberToString(this.vatRecord.stornoSupplierTaxDebtorTurnover.increaseBaseAmount20);
                if(this.vatRecord.stornoSupplierTaxDebtorTurnover.increaseVat20)
                    this.vatRecord.stornoSupplierTaxDebtorTurnover.increaseVat20 = this.formatNumberToString(this.vatRecord.stornoSupplierTaxDebtorTurnover.increaseVat20);
                if(this.vatRecord.stornoSupplierTaxDebtorTurnover.increaseBaseAmount10)
                    this.vatRecord.stornoSupplierTaxDebtorTurnover.increaseBaseAmount10 = this.formatNumberToString(this.vatRecord.stornoSupplierTaxDebtorTurnover.increaseBaseAmount10);
                if(this.vatRecord.stornoSupplierTaxDebtorTurnover.increaseVat10)
                    this.vatRecord.stornoSupplierTaxDebtorTurnover.increaseVat10 = this.formatNumberToString(this.vatRecord.stornoSupplierTaxDebtorTurnover.increaseVat10);
                if(this.vatRecord.stornoSupplierTaxDebtorTurnover.reductionBaseAmount20)
                    this.vatRecord.stornoSupplierTaxDebtorTurnover.reductionBaseAmount20 = this.formatNumberToString(this.vatRecord.stornoSupplierTaxDebtorTurnover.reductionBaseAmount20);
                if(this.vatRecord.stornoSupplierTaxDebtorTurnover.reductionVat20)
                    this.vatRecord.stornoSupplierTaxDebtorTurnover.reductionVat20 = this.formatNumberToString(this.vatRecord.stornoSupplierTaxDebtorTurnover.reductionVat20);
                if(this.vatRecord.stornoSupplierTaxDebtorTurnover.reductionBaseAmount10)
                    this.vatRecord.stornoSupplierTaxDebtorTurnover.reductionBaseAmount10 = this.formatNumberToString(this.vatRecord.stornoSupplierTaxDebtorTurnover.reductionBaseAmount10);
                if(this.vatRecord.stornoSupplierTaxDebtorTurnover.reductionVat10)
                    this.vatRecord.stornoSupplierTaxDebtorTurnover.reductionVat10 = this.formatNumberToString(this.vatRecord.stornoSupplierTaxDebtorTurnover.reductionVat10);
                if(this.vatRecord.stornoSupplierTaxDebtorTurnover.prepaymentReductionBaseAmount20)
                    this.vatRecord.stornoSupplierTaxDebtorTurnover.prepaymentReductionBaseAmount20 = this.formatNumberToString(this.vatRecord.stornoSupplierTaxDebtorTurnover.prepaymentReductionBaseAmount20);
                if(this.vatRecord.stornoSupplierTaxDebtorTurnover.prepaymentReductionVat20)
                    this.vatRecord.stornoSupplierTaxDebtorTurnover.prepaymentReductionVat20 = this.formatNumberToString(this.vatRecord.stornoSupplierTaxDebtorTurnover.prepaymentReductionVat20);
                if(this.vatRecord.stornoSupplierTaxDebtorTurnover.prepaymentReductionBaseAmount10)
                    this.vatRecord.stornoSupplierTaxDebtorTurnover.prepaymentReductionBaseAmount10 = this.formatNumberToString(this.vatRecord.stornoSupplierTaxDebtorTurnover.prepaymentReductionBaseAmount10);
                if(this.vatRecord.stornoSupplierTaxDebtorTurnover.prepaymentReductionVat10)
                    this.vatRecord.stornoSupplierTaxDebtorTurnover.prepaymentReductionVat10 = this.formatNumberToString(this.vatRecord.stornoSupplierTaxDebtorTurnover.prepaymentReductionVat10);

                if(this.vatRecord.stornoReceiverTaxDebtorTurnover.increaseBaseAmount20)
                    this.vatRecord.stornoReceiverTaxDebtorTurnover.increaseBaseAmount20 = this.formatNumberToString(this.vatRecord.stornoReceiverTaxDebtorTurnover.increaseBaseAmount20);
                if(this.vatRecord.stornoReceiverTaxDebtorTurnover.increaseVat20)
                    this.vatRecord.stornoReceiverTaxDebtorTurnover.increaseVat20 = this.formatNumberToString(this.vatRecord.stornoReceiverTaxDebtorTurnover.increaseVat20);
                if(this.vatRecord.stornoReceiverTaxDebtorTurnover.increaseBaseAmount10)
                    this.vatRecord.stornoReceiverTaxDebtorTurnover.increaseBaseAmount10 = this.formatNumberToString(this.vatRecord.stornoReceiverTaxDebtorTurnover.increaseBaseAmount10);
                if(this.vatRecord.stornoReceiverTaxDebtorTurnover.increaseVat10)
                    this.vatRecord.stornoReceiverTaxDebtorTurnover.increaseVat10 = this.formatNumberToString(this.vatRecord.stornoReceiverTaxDebtorTurnover.increaseVat10);
                if(this.vatRecord.stornoReceiverTaxDebtorTurnover.reductionBaseAmount20)
                    this.vatRecord.stornoReceiverTaxDebtorTurnover.reductionBaseAmount20 = this.formatNumberToString(this.vatRecord.stornoReceiverTaxDebtorTurnover.reductionBaseAmount20);
                if(this.vatRecord.stornoReceiverTaxDebtorTurnover.reductionVat20)
                    this.vatRecord.stornoReceiverTaxDebtorTurnover.reductionVat20 = this.formatNumberToString(this.vatRecord.stornoReceiverTaxDebtorTurnover.reductionVat20);
                if(this.vatRecord.stornoReceiverTaxDebtorTurnover.reductionBaseAmount10)
                    this.vatRecord.stornoReceiverTaxDebtorTurnover.reductionBaseAmount10 = this.formatNumberToString(this.vatRecord.stornoReceiverTaxDebtorTurnover.reductionBaseAmount10);
                if(this.vatRecord.stornoReceiverTaxDebtorTurnover.reductionVat10)
                    this.vatRecord.stornoReceiverTaxDebtorTurnover.reductionVat10 = this.formatNumberToString(this.vatRecord.stornoReceiverTaxDebtorTurnover.reductionVat10);
                if(this.vatRecord.stornoReceiverTaxDebtorTurnover.prepaymentReductionBaseAmount20)
                    this.vatRecord.stornoReceiverTaxDebtorTurnover.prepaymentReductionBaseAmount20 = this.formatNumberToString(this.vatRecord.stornoReceiverTaxDebtorTurnover.prepaymentReductionBaseAmount20);
                if(this.vatRecord.stornoReceiverTaxDebtorTurnover.prepaymentReductionVat20)
                    this.vatRecord.stornoReceiverTaxDebtorTurnover.prepaymentReductionVat20 = this.formatNumberToString(this.vatRecord.stornoReceiverTaxDebtorTurnover.prepaymentReductionVat20);
                if(this.vatRecord.stornoReceiverTaxDebtorTurnover.prepaymentReductionBaseAmount10)
                    this.vatRecord.stornoReceiverTaxDebtorTurnover.prepaymentReductionBaseAmount10 = this.formatNumberToString(this.vatRecord.stornoReceiverTaxDebtorTurnover.prepaymentReductionBaseAmount10);
                if(this.vatRecord.stornoReceiverTaxDebtorTurnover.prepaymentReductionVat10)
                    this.vatRecord.stornoReceiverTaxDebtorTurnover.prepaymentReductionVat10 = this.formatNumberToString(this.vatRecord.stornoReceiverTaxDebtorTurnover.prepaymentReductionVat10);
            },
            format(item) {
                console.log('Item');
                console.log(item);
                if(this.vatRecord.prepaymentAmount20)
                    this.vatRecord.prepaymentAmount20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.prepaymentAmount20));
                if(this.vatRecord.prepaymentBaseAmount20)
                    this.vatRecord.prepaymentBaseAmount20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.prepaymentBaseAmount20));
                if(this.vatRecord.prepaymentCalculatedVat20)
                    this.vatRecord.prepaymentCalculatedVat20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.prepaymentCalculatedVat20));
                if(this.vatRecord.prepaymentAmount10)
                    this.vatRecord.prepaymentAmount10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.prepaymentAmount10));
                if(this.vatRecord.prepaymentBaseAmount10)
                    this.vatRecord.prepaymentBaseAmount10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.prepaymentBaseAmount10));
                if(this.vatRecord.prepaymentCalculatedVat10)
                    this.vatRecord.prepaymentCalculatedVat10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.prepaymentCalculatedVat10));

                if(this.vatRecord.baseAmount20)
                    this.vatRecord.baseAmount20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.baseAmount20));
                if(this.vatRecord.calculatedVat20)
                    this.vatRecord.calculatedVat20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.calculatedVat20));
                if(this.vatRecord.baseAmount10)
                    this.vatRecord.baseAmount10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.baseAmount10));
                if(this.vatRecord.calculatedVat10)
                    this.vatRecord.calculatedVat10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.calculatedVat10));
                if(this.vatRecord.noChargeBaseAmount20)
                    this.vatRecord.noChargeBaseAmount20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.noChargeBaseAmount20));
                if(this.vatRecord.noChargeCalculatedVat20)
                    this.vatRecord.noChargeCalculatedVat20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.noChargeCalculatedVat20));
                if(this.vatRecord.noChargeBaseAmount10)
                    this.vatRecord.noChargeBaseAmount10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.noChargeBaseAmount10));
                if(this.vatRecord.noChargeCalculatedVat10)
                    this.vatRecord.noChargeCalculatedVat10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.noChargeCalculatedVat10));
                if(this.vatRecord.touristServicesBaseAmount20)
                    this.vatRecord.touristServicesBaseAmount20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.touristServicesBaseAmount20));
                if(this.vatRecord.touristServicesCalculatedVat20)
                    this.vatRecord.touristServicesCalculatedVat20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.touristServicesCalculatedVat20));
                if(this.vatRecord.prepaymentTouristServicesBaseAmount20)
                    this.vatRecord.prepaymentTouristServicesBaseAmount20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.prepaymentTouristServicesBaseAmount20));
                if(this.vatRecord.prepaymentTouristServicesCalculatedVat20)
                    this.vatRecord.prepaymentTouristServicesCalculatedVat20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.prepaymentTouristServicesCalculatedVat20));
                if(this.vatRecord.artAntiquesCollectibleGoodsBaseAmount20)
                    this.vatRecord.artAntiquesCollectibleGoodsBaseAmount20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.artAntiquesCollectibleGoodsBaseAmount20));
                if(this.vatRecord.artAntiquesCollectibleGoodsCalculatedVat20)
                    this.vatRecord.artAntiquesCollectibleGoodsCalculatedVat20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.artAntiquesCollectibleGoodsCalculatedVat20));
                if(this.vatRecord.artAntiquesCollectibleGoodsBaseAmount10)
                    this.vatRecord.artAntiquesCollectibleGoodsBaseAmount10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.artAntiquesCollectibleGoodsBaseAmount10));
                if(this.vatRecord.artAntiquesCollectibleGoodsCalculatedVat10)
                    this.vatRecord.artAntiquesCollectibleGoodsCalculatedVat10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.artAntiquesCollectibleGoodsCalculatedVat10));
                if(this.vatRecord.prepaymentArtAntiquesCollectibleGoodsBaseAmount20)
                    this.vatRecord.prepaymentArtAntiquesCollectibleGoodsBaseAmount20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.prepaymentArtAntiquesCollectibleGoodsBaseAmount20));
                if(this.vatRecord.prepaymentArtAntiquesCollectibleGoodsCalculatedVat20)
                    this.vatRecord.prepaymentArtAntiquesCollectibleGoodsCalculatedVat20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.prepaymentArtAntiquesCollectibleGoodsCalculatedVat20));
                if(this.vatRecord.prepaymentArtAntiquesCollectibleGoodsBaseAmount10)
                    this.vatRecord.prepaymentArtAntiquesCollectibleGoodsBaseAmount10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.prepaymentArtAntiquesCollectibleGoodsBaseAmount10));
                if(this.vatRecord.prepaymentArtAntiquesCollectibleGoodsCalculatedVat10)
                    this.vatRecord.prepaymentArtAntiquesCollectibleGoodsCalculatedVat10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.prepaymentArtAntiquesCollectibleGoodsCalculatedVat10));
                if(this.vatRecord.touristServicesBaseAmount20)
                    this.vatRecord.touristServicesBaseAmount20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.vatRecord.touristServicesBaseAmount20));
                if(item) {
                    if(item.increaseBaseAmount20)
                        item.increaseBaseAmount20 = this.formatNumberToString(this.convertFormattedStringToNumber(item.increaseBaseAmount20));
                    if(item.increaseVat20)
                        item.increaseVat20 = this.formatNumberToString(this.convertFormattedStringToNumber(item.increaseVat20));
                    if(item.increaseBaseAmount10)
                        item.increaseBaseAmount10 = this.formatNumberToString(this.convertFormattedStringToNumber(item.increaseBaseAmount10));
                    if(item.increaseVat10)
                        item.increaseVat10 = this.formatNumberToString(this.convertFormattedStringToNumber(item.increaseVat10));
                    if(item.reductionBaseAmount20)
                        item.reductionBaseAmount20 = this.formatNumberToString(this.convertFormattedStringToNumber(item.reductionBaseAmount20));
                    if(item.reductionVat20)
                        item.reductionVat20 = this.formatNumberToString(this.convertFormattedStringToNumber(item.reductionVat20));
                    if(item.reductionBaseAmount10)
                        item.reductionBaseAmount10 = this.formatNumberToString(this.convertFormattedStringToNumber(item.reductionBaseAmount10));
                    if(item.reductionVat10)
                        item.reductionVat10 = this.formatNumberToString(this.convertFormattedStringToNumber(item.reductionVat10));
                    if(item.prepaymentReductionBaseAmount20)
                        item.prepaymentReductionBaseAmount20 = this.formatNumberToString(this.convertFormattedStringToNumber(item.prepaymentReductionBaseAmount20));
                    if(item.prepaymentReductionVat20)
                        item.prepaymentReductionVat20 = this.formatNumberToString(this.convertFormattedStringToNumber(item.prepaymentReductionVat20));
                    if(item.prepaymentReductionBaseAmount10)
                        item.prepaymentReductionBaseAmount10 = this.formatNumberToString(this.convertFormattedStringToNumber(item.prepaymentReductionBaseAmount10));
                    if(item.prepaymentReductionVat10)
                        item.prepaymentReductionVat10 = this.formatNumberToString(this.convertFormattedStringToNumber(item.prepaymentReductionVat10));
                }
            },
            clearTurnoverDate() {
                this.vatRecord.turnoverDate = '';
            },
            getVatPeriodTypeText(item) {
                if(i18n.locale === 'eng')
                    return item.typeEng;
                else
                    return item.type;
            },
            getVatPeriodText(item) {
                if(i18n.locale === 'eng')
                    return item.periodEng;
                else
                    return item.periodSrb;
            },
            documentStatusText() {
                if(localStorage.getItem('language') === 'eng')
                    return this.vatRecord.status.vatRecordStatusEng;
                else
                    return this.vatRecord.status.vatRecordStatus;
            },            
            beforeWindowUnload(e) {
                e.preventDefault()
                e.returnValue = ''
            },
        }
    }

</script>
